import { type FC, useRef, useState } from "react";
import { Button, Slider, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Player, formatPlayerTime } from "@/shared/components/Player";
import type { Stream, StreamContent } from "@/partner/entities/streams";
import { useCreateStreamClipMutation } from "@/partner/entities/streams";

// Cloudflare's instant-clip ceiling. Clips are now cut via /stream/copy fed
// with the signed clip.mp4 URL (see backend
// PlaybackTokenService.INSTANT_CLIP_MAX_DURATION_SECONDS / ClipService), so
// this bounds the clip itself, not just its preview — a window longer than
// this is impossible via this route.
const MAX_CLIP_DURATION_SECONDS = 60;

interface StreamStudioRecordingProps {
  content: StreamContent;
  stream: Stream;
  onCreated: () => void;
}

export const StreamStudioRecording: FC<StreamStudioRecordingProps> = ({
  content,
  stream,
  onCreated,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Tracks the element + listener currently attached, so the cleanup call
  // (Player invokes getVideoElement(null) on unmount/src change) can detach
  // it — the null call itself carries no element reference to remove from.
  const attachedVideoRef = useRef<HTMLVideoElement | null>(null);
  const onLoadedRef = useRef<(() => void) | null>(null);
  const [duration, setDuration] = useState(0);
  const [range, setRange] = useState<[number, number]>([0, 0]);

  const [createStreamClip, { loading }] = useCreateStreamClipMutation({
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });

  const [start, end] = range;

  const cut = async () => {
    const { data } = await createStreamClip({
      variables: {
        input: {
          contentId: content.id,
          startSeconds: Math.round(start),
          // Окно уже не длиннее MAX_CLIP_DURATION_SECONDS — слайдер и поля
          // ввода не пускают дальше (см. ниже), а кнопка выключена сверх
          // потолка на всякий случай.
          durationSeconds: Math.round(end - start),
          title: t("streams.studio.defaultClipTitle"),
        },
      },
    });
    if (!data) return;
    enqueueSnackbar(t("streams.studio.createdSnackbar"), {
      variant: "success",
    });
    onCreated();
  };

  // Ввод «мм:сс» (или «ч:мм:сс» для записей длиннее часа) в обе стороны со
  // слайдером — обратная операция к formatPlayerTime, которая переключается
  // на «ч:мм:сс» при seconds >= 3600.
  const parseTime = (value: string): number | null => {
    const trimmed = value.trim();
    const short = /^(\d+):([0-5]\d)$/.exec(trimmed);
    if (short) return Number(short[1]) * 60 + Number(short[2]);
    const long = /^(\d+):([0-5]\d):([0-5]\d)$/.exec(trimmed);
    if (long) {
      return Number(long[1]) * 3600 + Number(long[2]) * 60 + Number(long[3]);
    }
    return null;
  };

  const setBound = (index: 0 | 1) => (value: string) => {
    const seconds = parseTime(value);
    if (seconds === null || seconds < 0 || seconds > duration) return;
    setRange((prev) => {
      const next: [number, number] =
        index === 0
          ? [Math.min(seconds, prev[1]), prev[1]]
          : [prev[0], Math.max(seconds, prev[0])];
      if (next[1] - next[0] > MAX_CLIP_DURATION_SECONDS) {
        // Не молчим: без пояснения непонятно, почему введённое значение не
        // применилось.
        enqueueSnackbar(
          t("streams.studio.clipTooLong", { max: MAX_CLIP_DURATION_SECONDS }),
          { variant: "warning" }
        );
        return prev;
      }
      return next;
    });
  };

  return (
    <Stack spacing={2}>
      <Player
        src={stream.recordPlayback?.hlsUrl ?? null}
        poster={stream.recordPlayback?.thumbnailUrl ?? undefined}
        hotkeys={false}
        getVideoElement={(video) => {
          videoRef.current = video;
          // Always detach the previous listener first — covers both the
          // cleanup call (video === null) and a src change (new element).
          if (attachedVideoRef.current && onLoadedRef.current) {
            attachedVideoRef.current.removeEventListener(
              "loadedmetadata",
              onLoadedRef.current
            );
            attachedVideoRef.current = null;
            onLoadedRef.current = null;
          }
          if (!video) return;
          const onLoaded = () => {
            const total = video.duration || 0;
            setDuration(total);
            setRange([0, Math.min(total, 30)]);
          };
          video.addEventListener("loadedmetadata", onLoaded);
          attachedVideoRef.current = video;
          onLoadedRef.current = onLoaded;
        }}
      />

      <Slider
        value={range}
        min={0}
        max={duration || 1}
        disableSwap
        onChange={(_event, value, activeThumb) => {
          let next = value as [number, number];
          // Тащим за один из маркеров дальше 60с — подтягиваем другой край,
          // а не расширяем окно за потолок Cloudflare.
          if (next[1] - next[0] > MAX_CLIP_DURATION_SECONDS) {
            next =
              activeThumb === 0
                ? [next[1] - MAX_CLIP_DURATION_SECONDS, next[1]]
                : [next[0], next[0] + MAX_CLIP_DURATION_SECONDS];
          }
          setRange(next);
          // Перематываем к перетаскиваемой границе — видно, что режем.
          if (videoRef.current) videoRef.current.currentTime = next[0];
        }}
        valueLabelDisplay="auto"
        valueLabelFormat={(value) => formatPlayerTime(value)}
      />

      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          size="small"
          label={t("streams.studio.startField")}
          defaultValue={formatPlayerTime(start)}
          key={`start-${Math.round(start)}`}
          onBlur={(event) => setBound(0)(event.target.value)}
        />
        <TextField
          size="small"
          label={t("streams.studio.endField")}
          defaultValue={formatPlayerTime(end)}
          key={`end-${Math.round(end)}`}
          onBlur={(event) => setBound(1)(event.target.value)}
        />
        <Typography variant="body2" color="text.secondary">
          {formatPlayerTime(Math.max(0, end - start))}
        </Typography>
        <Button
          variant="contained"
          disabled={
            end <= start || end - start > MAX_CLIP_DURATION_SECONDS || loading
          }
          onClick={() => void cut()}
        >
          {t("streams.studio.cutButton")}
        </Button>
      </Stack>
    </Stack>
  );
};
